import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn'
import { useEscapeKey } from '@/hooks/use-escape-key'
import { useExitTransition } from '@/hooks/use-exit-transition'
import { selectTopOverlay, useOverlayStore, type OverlayName } from '@/store/overlays'

/** Matches the exit animation in _overlay.scss. */
const EXIT_MS = 220

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface OverlayProps {
  name: OverlayName
  open: boolean
  onClose: () => void
  children: ReactNode
  /** Accessible name for the dialog. */
  label: string
}

/**
 * Replaces `<q-overlay>` from the quasar-app-extension-qoverlay.
 *
 * Beyond the port it adds what the original lacked: the panel rises in and
 * settles out instead of blinking (the exit is why this component owns its own
 * mount lifetime rather than being rendered conditionally), the dialog is
 * announced to assistive tech, focus moves into it and is restored on close,
 * Escape only closes the topmost overlay, and background scrolling is locked
 * while it is open — the Quasar usage passed `:no-scroll="false"`, so the page
 * behind kept scrolling under the modal.
 */
export function Overlay({ name, open, onClose, children, label }: OverlayProps) {
  const { isMounted, isClosing } = useExitTransition(open, EXIT_MS)
  const isTop = useOverlayStore(selectTopOverlay) === name

  const containerRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEscapeKey(onClose, isTop && open)

  // Lock the page behind the modal and restore focus when the last one closes.
  useEffect(() => {
    if (!isMounted) return

    previouslyFocused.current = document.activeElement as HTMLElement | null
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    // Defer so the content has rendered before we look for something to focus.
    const id = window.setTimeout(() => {
      const focusable = containerRef.current?.querySelector<HTMLElement>(FOCUSABLE)
      ;(focusable ?? containerRef.current)?.focus({ preventScroll: true })
    }, 0)

    return () => {
      window.clearTimeout(id)
      document.body.style.overflow = overflow
      previouslyFocused.current?.focus?.()
    }
  }, [isMounted])

  // Keep Tab inside the dialog.
  useEffect(() => {
    if (!isTop || !open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !containerRef.current) return
      const focusable = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((element) => element.offsetParent !== null)
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isTop, open])

  if (!isMounted) return null

  return createPortal(
    <div
      className={cn('overlay-container', isClosing && 'is-closing')}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      ref={containerRef}
      tabIndex={-1}
      onMouseDown={(event) => {
        // Click-outside closes, matching the backdrop behaviour of q-overlay.
        if (event.target === event.currentTarget) onClose()
      }}
    >
      {children}
    </div>,
    document.body,
  )
}
